<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Submission Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading class="mb-3">
      <span class="float-left mr-2">{{ submission.publicNumber() }}</span>
      <badge-batch :badge_collection="submission_badges" />
      <template #right-content>
        <header-button-group
          :button_config="button_config"
          @buttonAction="releaseSubmission"
        >
          <template #post_buttons>
            <button
              v-show="checkPermission('delete submission')"
              title="Delete submission"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read submission'">
      <template #gated-content>
        <status-progress
          :current_status="current_status"
          :log_collection="submission_status_logs"
          status_group="submission"
          class="mb-3"
        />
        <context-pills :items="submission_items" class="mb-3" />
        <submission-card v-if="submission" :submission="submission" />
        <submission-detail-cardstack />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BadgeBatch from "@/components/shared/BadgeBatch";
import BasicHeading from "@/components/shared/BasicHeading";
import ContextPills from "@/components/shared/ContextPills";
import GeneralTile from "@/components/shared/GeneralTile";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import LinkTile from "@/components/shared/LinkTile";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import StatusProgress from "@/components/shared/StatusProgress";
import SubmissionCard from "@/components/lab/submission/SubmissionCard";
import SubmissionDetailCardstack from "@/components/lab/submission/SubmissionDetailCardstack";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { submissionNotifications } from "@/shared/notifications";
import { notificationOptions } from "@/shared/toast-notification-options";
import SubmissionExtensionTableDTO from "@/DTO/ExtensionTables/SubmissionExtensionTableDTO";

Vue.use(VueNotifications, notificationOptions);

const SubmissionRepository = LabRepositoryFactory.get("submission");

export default {
  name: "SubmissionDetail",
  components: {
    BadgeBatch,
    BasicHeading,
    ContextPills,
    GeneralTile,
    HeaderButtonGroup,
    LinkTile,
    PermissionAlertGate,
    StatusProgress,
    SubmissionCard,
    SubmissionDetailCardstack,
  },
  mixins: [ToastNotifications],
  data() {
    return {
      loading: true,
      submission_id: this.$route.params.submission_id || null,
      submission: null,
      submission_badges: null,
      submission_items: null,
      submission_status_logs: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/submissions",
            title: "Submission list",
          },
          {
            icon: "fa-pencil",
            linkto: `/submissions/${this.submission_id}/edit`,
            title: "Edit submission",
            variant: "warning",
          },
          {
            with_action: true,
            action_data: "release",
            variant: "success",
            text: "Release",
          },
        ],
        dropdown: {
          items: [
            {
              icon: "fa-layer-group",
              linkto: `/submissions/${this.submission_id}/report`,
              text: "Submission report",
              variant: "info",
            },
            {
              icon: "fa-shovel",
              linkto: `/submissions/${this.submission_id}/apply-test-package`,
              text: "Apply test package",
              variant: "info",
            }
          ],
        },
      };
    },
    current_status() {
      return (
        this.submission &&
        this.submission.current_submission_status_log &&
        this.submission.current_submission_status_log.submission_status.status
      );
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    mergeUpdateFields() {
      const updatedFields = SubmissionExtensionTableDTO.run([this.submission]);
      if (updatedFields[0]) {
        this.submission.set(updatedFields[0].fields);
      }
    },
    initSubmission() {
      this.mergeUpdateFields();
      this.submission_badges = [
        this.submission.event_code,
        this.submission.identifier,
      ];
      this.submission_status_logs = this.submission.getSubmissionStatusLogs();
      this.submission_items = [];

      const project = this.submission.getProject();
      const sample_type = this.submission.getSampleType();
      const test_package = this.submission.getTestPackage();
      const turnaround = this.submission.getTurnaround();

      if (project.id) {
        const { customer, customer_contact, customer_location } = project;

        this.submission_items = [
          ...this.submission_items,
          {
            to: `/projects/${project.id}`,
            faIcon: project.icon(),
            variant: "dark",
            label: project.publicNumber(),
          },
        ];

        customer &&
          (this.submission_items = [
            ...this.submission_items,
            {
              to: `/customer/${customer.id}`,
              faIcon: project.getCustomer().icon(),
              variant: "dark",
              label: customer.full_name,
            },
          ]);
        customer_contact &&
          customer_contact.name &&
          (this.submission_items = [
            ...this.submission_items,
            {
              to: `/customer-contact/${customer_contact.id}`,
              faIcon: project.getCustomerContact().icon(),
              variant: "dark",
              label: customer_contact.name,
            },
          ]);
        customer_location &&
          (this.submission_items = [
            ...this.submission_items,
            {
              to: `/customer-location/${customer_location.id}`,
              faIcon: project.getCustomerLocation().icon(),
              variant: "dark",
              label: customer_location.name,
            },
          ]);

        this.submission_items = [
          ...this.submission_items,
          {
            to: `/submissions/${this.submission.id}/labels`,
            faIcon: "tags",
            variant: "dark",
            label: "Print receiving labels",
          },
        ];
      }

      sample_type.id &&
        (this.submission_items = [
          ...this.submission_items,
          {
            to: `/sampletypes/${sample_type.id}`,
            faIcon: sample_type.icon(),
            variant: "dark",
            label: sample_type.name,
          },
        ]);
      test_package.id &&
        (this.submission_items = [
          ...this.submission_items,
          {
            to: `/testpackages/${test_package.id}`,
            faIcon: test_package.icon(),
            variant: "dark",
            label: test_package.name,
          },
        ]);
      turnaround.id &&
        (this.submission_items = [
          ...this.submission_items,
          {
            to: `/turnarounds/${turnaround.id}`,
            faIcon: turnaround.icon(),
            variant: "dark",
            label: turnaround.name,
          },
        ]);

      this.submission_items.push({
        to: `/submissions/${this.submission_id}/audit-trail`,
        faIcon: "search",
        variant: "dark",
        label: "Audit Trail",
      })
    },
    releaseSubmission() {
      this.submission.releaseSubmission().then(() => {
        this.quickSuccessToast("Release");
      });
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this submission. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await SubmissionRepository.suspend(this.submission_id);
  
              this.deleteSuccess();
              this.$router.push("/submissions");
            } catch ({ response }) {
              const { status, data } = response.response;
  
              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.submission = await getModel({
      model_type: "submission",
      id: this.submission_id,
      message: this.$bvToast,
    });

    if (this.submission) {
      this.initSubmission();

      this.loading = false;
    } else {
      this.$router.back();
    }
  },
  notifications: submissionNotifications,
};
</script>
